import { useState, useEffect, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { X, Calendar, ClipboardCheck, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackDemoClick, trackTrialClick, trackEvent } from "@/lib/analytics";

const SESSION_KEY = "ch_scroll_cta_shown";
const DISMISS_KEY = "ch_scroll_cta_dismissed";
const DISMISS_HOURS = 24;
const SCROLL_THRESHOLD = 0.5; // 50% scroll depth

type CTAVariant = "demo" | "audit" | "maturity";

interface CTAConfig {
  icon: React.ElementType;
  title: string;
  description: string;
  buttonText: string;
  link: string;
  trackingId: string;
}

const CTA_CONFIGS: Record<CTAVariant, CTAConfig> = {
  demo: {
    icon: Calendar,
    title: "See ComplyHub in Action",
    description: "Book a personalised demo and discover how RTOs like yours manage compliance with confidence.",
    buttonText: "Book a Demo",
    link: "/demo#book",
    trackingId: "scroll_trigger_demo",
  },
  audit: {
    icon: ClipboardCheck,
    title: "Check Your Audit Readiness",
    description: "Get an indicative view of your compliance system with our free Compliance Scorecard.",
    buttonText: "See Audit Readiness",
    link: "/auditors",
    trackingId: "scroll_trigger_audit",
  },
  maturity: {
    icon: Rocket,
    title: "How Mature Is Your Compliance?",
    description: "Take our 3-minute check to identify your compliance gaps before the auditor does.",
    buttonText: "Check Your Compliance",
    link: "/compliance-scorecard/start",
    trackingId: "scroll_trigger_maturity",
  },
};

function getVariantForRoute(pathname: string): CTAVariant {
  // Disable on pricing page to avoid overlapping plan cards
  if (pathname.startsWith("/pricing")) {
    return "demo"; // Still return a variant but we'll suppress it
  }
  // Product pages → Demo
  if (pathname.startsWith("/product") || pathname.startsWith("/features")) {
    return "demo";
  }
  // Auditors/compliance pages → Audit readiness
  if (pathname.startsWith("/auditors") || pathname.startsWith("/compliance")) {
    return "audit";
  }
  // Blog pages → Maturity check
  if (pathname.startsWith("/blog") || pathname.match(/^\/[a-z]+-[a-z]+/)) {
    return "maturity";
  }
  // Default → Demo
  return "demo";
}

/**
 * ScrollTriggerCTA - Context-aware slide-in CTA triggered at 50% scroll
 * Once per session, 24-hour cooldown after dismiss
 */
export function ScrollTriggerCTA() {
  const [isVisible, setIsVisible] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const location = useLocation();

  const variant = getVariantForRoute(location.pathname);
  const config = CTA_CONFIGS[variant];

  // Check if dismissed recently
  const isDismissed = useCallback(() => {
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (!dismissedAt) return false;
    
    const dismissedTime = parseInt(dismissedAt, 10);
    const hoursSince = (Date.now() - dismissedTime) / (1000 * 60 * 60);
    return hoursSince < DISMISS_HOURS;
  }, []);

  // Check if already shown this session
  const hasShownThisSession = useCallback(() => {
    return sessionStorage.getItem(SESSION_KEY) === "true";
  }, []);

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    trackEvent("scroll_cta_dismiss", { variant });
  }, [variant]);

  const handleCTAClick = useCallback(() => {
    if (variant === "demo") {
      trackDemoClick(config.trackingId);
    } else if (variant === "maturity") {
      trackEvent("maturity_check_click", { source: config.trackingId });
    } else {
      trackTrialClick(config.trackingId);
    }
    handleDismiss();
  }, [variant, config.trackingId, handleDismiss]);

  useEffect(() => {
    // Don't trigger if already shown, dismissed, triggered, or on pricing page
    if (hasShownThisSession() || isDismissed() || hasTriggered || location.pathname.startsWith("/pricing") || location.pathname.startsWith("/compliance-scorecard")) {
      return;
    }

    // Also respect ExitIntentPopup dismissal
    const exitDismissed = localStorage.getItem("ch_exit_dismissed");
    if (exitDismissed) {
      const dismissedTime = parseInt(exitDismissed, 10);
      const daysSince = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
      if (daysSince < 1) return; // Don't show if exit popup was dismissed in last 24h
    }

    const handleScroll = () => {
      if (hasTriggered) return;

      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = window.scrollY / scrollHeight;

      if (scrollPercent >= SCROLL_THRESHOLD) {
        setHasTriggered(true);
        sessionStorage.setItem(SESSION_KEY, "true");
        
        // Small delay for smoother experience
        setTimeout(() => setIsVisible(true), 500);
        trackEvent("scroll_cta_trigger", { 
          variant, 
          scroll_percent: Math.round(scrollPercent * 100) 
        });
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasTriggered, isDismissed, hasShownThisSession, variant]);

  if (!isVisible) return null;

  const IconComponent = config.icon;

  return (
    <div className="fixed bottom-4 right-4 z-[90] max-w-sm animate-slide-in-right">
      {/* Card */}
      <div className="bg-background rounded-2xl shadow-2xl border border-border/50 overflow-hidden">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-foreground/5 transition-colors z-10"
          aria-label="Close"
        >
          <X className="w-4 h-4 text-foreground/50" />
        </button>

        {/* Header accent */}
        <div className="h-1 bg-gradient-to-r from-ch-purple to-ch-fuchsia" />

        {/* Content */}
        <div className="p-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-ch-purple/10 flex items-center justify-center shrink-0">
              <IconComponent className="w-6 h-6 text-ch-purple" />
            </div>
            <div className="flex-1 min-w-0 pr-4">
              <h4 className="font-semibold text-foreground mb-1">
                {config.title}
              </h4>
              <p className="text-sm text-foreground/70 mb-4">
                {config.description}
              </p>
              <Button
                asChild
                className="bg-ch-cyan hover:bg-ch-cyan/90 text-white font-medium"
                onClick={handleCTAClick}
              >
                <Link to={config.link}>
                  {config.buttonText}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ScrollTriggerCTA;
